import * as Yup from 'yup';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'redux/store';

// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Stack, IconButton, InputAdornment, Box } from '@mui/material';
import { LoadingButton } from '@mui/lab';

// hooks
import useAuth from 'hooks/useAuth';
import useRegexPassword from 'hooks/useRegexPassword';
// components
import { FormProvider, RHFPasswordTextField, RHFTextField } from 'components/hook-form';
import PasswordInfo from 'components/PasswordInfo';
import ConfirmModal from 'components/ConfirmModal';

import { getAccount, testPasswordIsValid } from 'redux/slices/User';
import { getAllSetting } from 'redux/slices/All';

import { ReactComponent as EyeOffIcon } from 'assets/images/svg/eye_off.svg';
import { ReactComponent as EyeOnIcon } from 'assets/images/svg/eye_on.svg';
import maxLength from "maxLength";

// ----------------------------------------------------------------------

export default function ResetPasswordForm({ handleSubmitAccount }) {
  const { user: { usercode, username, userfirstname, logincode }, logout } = useAuth();
  const { user: { account }, all: { allSetting } } = useSelector((state) => state);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const regexPassword = useRegexPassword();
  const confirmQuitRef = useRef(null);
  const openConfirm = () => {
    confirmQuitRef.current.handleOpen();
  };
  const resetSchemat = Yup.object().shape({
    oldpassword: Yup.string().required(t("errors.required")).matches(regexPassword, t("errors.invalidOldPassword")).trim()
      .test({
        name: 'test-oldPassword',
        message: t("errors.invalidOldPassword"),
        test: async (oldpassword) => {
          const bool = await testPasswordIsValid({ staffcode: usercode, password: oldpassword, nbHistoDiffPassword: 1 });
          return !bool;
        }

      }),
    newpassword1: Yup.string().required(t("errors.required"))
      .matches(regexPassword, t("errors.invalidPassword")).trim()
      .test({
        name: 'test-nbHistoDiffPassword',
        message: t("errors.invalidPassword"),
        test: async (newpassword1, testContext) => {
          if (testContext.parent.oldpassword === newpassword1) {
            return false;
          }
          if (newpassword1 !== "") {
            const bool = await testPasswordIsValid({ staffcode: usercode, password: newpassword1, nbHistoDiffPassword: allSetting?.MDP?.MDP_DIFFERENT_DES_DERNIERS?.valeur });
            return bool;
          }
          return true;
        }

      }),

    newpassword2: Yup.string()
      .required(t("errors.required"))
      .test({
        name: 'test-equalNewPassword',
        message: t("errors.equalNewPassword"),
        test: async (newpassword2, testContext) => {
          if (testContext.parent.newpassword1 !== newpassword2) return false;
          return true;
        }

      })
  });

  const methods = useForm({
    reValidateMode: 'onBlur',
    resolver: yupResolver(resetSchemat)
  });

  const {
    handleSubmit,
    watch,
    reset,
    formState: { errors }
  } = methods;

  const values = watch();

  const onSubmit = async () => {
    handleSubmitAccount(values);
  };


  useEffect(() => {
    const onResetAccount = () => {
      if (account) {
        reset({
          code: usercode,
          ...account
        });
      }
    };
    onResetAccount();
    // eslint-disable-next-line
  }, [account]);



  useEffect(() => {
    if (usercode) {
      dispatch(getAllSetting());
      dispatch(getAccount({ code: usercode }));
    }
  }, [dispatch, usercode]);

  const onAbandon = () => logout(usercode);

  const passwordWatch = watch('newpassword1');
  const logincodeWatch = watch('logincode');

  const hasNbHistoDiffPasswordError = (!passwordWatch && account?.usercode) || errors?.newpassword1?.type === 'test-nbHistoDiffPassword';
  const hasLengthNotValid = passwordWatch?.length < allSetting?.MDP?.MDP_TAILLE_MIN?.valeur;
  const userInfos = (username && userfirstname) ? `${username} ${userfirstname} (${logincode})` : logincode;
  const isEqualToLogin = passwordWatch === logincodeWatch;

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(openConfirm)}>

      <Box className="wrapper_intro_form_tilte" display="flex" justifyContent="center">
        {t('reset.hello')} {userInfos}
      </Box>
      <Box className="form_message form_message_error bold" sx={{ textAlign: "center !important" }}>
        {t(`reset.error`)}
      </Box>
      <Stack p={2} spacing={3}>
        <RHFTextField
          name="oldpassword"
          placeholder={t('reset.oldPassword')}
          type={showOldPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowOldPassword(!showOldPassword)} edge="end">
                  {showOldPassword ? <EyeOffIcon /> : <EyeOnIcon />}
                </IconButton>
              </InputAdornment>
            )
          }} />
        <Box sx={{ position: "relative" }}>
          <RHFPasswordTextField
            name="newpassword1"
            placeholder={t('reset.newPassword')}
            maxLength={maxLength.user.password}
            type={showPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    {showPassword ? <EyeOffIcon /> : <EyeOnIcon />}

                  </IconButton>
                </InputAdornment>
              )
            }}
            length={allSetting?.MDP?.MDP_TAILLE_MIN?.valeur}
            isEqualToLogin={isEqualToLogin}
            hasNbHistoDiffPasswordError={hasNbHistoDiffPasswordError}
            hasLengthNotValid={hasLengthNotValid}
            nbOldPassword={allSetting?.MDP?.MDP_DIFFERENT_DES_DERNIERS?.valeur}
          />
          <Box sx={{ position: "absolute", right: -30, top: 6 }}>
            <PasswordInfo length={allSetting?.MDP?.MDP_TAILLE_MIN?.valeur} nbOldPassword={allSetting?.MDP?.MDP_DIFFERENT_DES_DERNIERS?.valeur} />
          </Box>
        </Box>

        <RHFTextField
          name="newpassword2"
          placeholder={t('reset.newPasswordConfirmation')}
          type={showConfirmPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                  {showConfirmPassword ? <EyeOffIcon /> : <EyeOnIcon />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Stack>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          '& > button': { minWidth: 150 }
        }}
      >
        <LoadingButton
          fullWidth
          color="primary"
          size="large"
          className="form_submit"
          variant="contained"
          style={{ marginRight: 3 }}
          onClick={onAbandon}
        >
          {t('buttons.abandon')}

        </LoadingButton>
        <LoadingButton
          type="submit"
          fullWidth
          size="large"
          className="form_submit green_button"
          variant="contained"
        >
          {t("buttons.valid")}

        </LoadingButton>
      </Box>
      <ConfirmModal ref={confirmQuitRef} onAccept={onSubmit} title={t("popup.titles.resetPassword")} message={t("popup.resetConfirmation", { login: account?.logincode })}> </ConfirmModal>

    </FormProvider>
  );
}
